import React, { useState } from 'react';
import { SideMenu } from '~/models';
import api from '~/_base/constants/api';
import useSWR, { mutate } from 'swr';
import { Link, useLocation } from 'react-router-dom';
import {
  Theme,
  Drawer,
  AppBar,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material';
import { IFileText, IFileEmpty, IClipboard2, IHome, IImage, IQuestion, IFolder, ILogout, IChat, IActionCollapse2, IActionExpand2 } from './helixIcons';
import ProjectMenu from './projectMenu';
import TopNavigation from './topNavigation';
import logger from '~/_base/logger';
import { t } from 'i18next';


function getSidenavIcon(name: string) {
  switch (name) {
    case 'project':
      return <IHome />;
    case 'memos':
      return <IFileEmpty />;
    case 'forms':
      return <IFileText />;
    case 'actions':
      return <IClipboard2 />;
    case 'drawings':
      return <IImage />;
    default:
      return undefined;
  }
}

const Navigation: React.FC<{
  sidebarData: SideMenu;
}> = props => {
  const location = useLocation();
  const { sidebarData } = props;
  const [open, setOpen] = useState(getSidebarCollapseState());
  const [projectDrawerOpen, setProjectDrawerOpen] = useState(false);
  const { data: currentContext } = useSWR(
    api.currentContext.address,
    api.currentContext.request
  );
  const selectedProject = currentContext?.current_project;
  const isProjectSelected = selectedProject != null;
  let selectedProjectText = selectedProject
    ? `${selectedProject.job_number} : ${selectedProject.name}`
    : t('No Project Selected');

  function isActive(pathname: string) {
    return location.pathname === pathname;
  }

  async function handleSignoutClick() {
    logger.info(`Side menu item "Sign Out" clicked`);
    await api.signout.request();
    try {
      sessionStorage.removeItem('auth_method');
    } catch (e) {
      console.error(e);
    }
    window.location.href = '/';
  }

  function getSidebarCollapseState() {
    const status = localStorage.getItem('ape_sidebar_collapse');
    return status ? JSON.parse(status) : false;
  }

  function handleToggleCollapse() {
    if (open && projectDrawerOpen) {
      setProjectDrawerOpen(false);
    }
    localStorage.setItem('ape_sidebar_collapse', JSON.stringify(!open));
    setOpen(!open);
  }

  return (
    <React.Fragment>
      <AppBar>
        <TopNavigation />
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: open ? 256 : 56,
          transition: theme => theme.transitions.create('width'),
        }}
        PaperProps={{
          sx: {
            width: open ? 256 : 56,
            transition: theme => theme.transitions.create('width'),
            top: 64,
            height: `calc(100% - 64px)`,
            overflowX: 'hidden',
            overflowY: 'auto',
          },
        }}
        open={open}
      >
        <List sx={{ flex: 1 }}>
          <ListItemButton
            onClick={() => {
              if (!projectDrawerOpen) {
                setOpen(true);
                mutate(api.projects.address);
                setTimeout(() => setProjectDrawerOpen(true), 150);
                logger.info('Project drawer menu toggle clicked (opened)');
                return;
              }
              setProjectDrawerOpen(false);
              logger.info('Project drawer menu toggle clicked (closed)');
            }}
          >
            <Tooltip title={selectedProjectText}>
              <ListItemIcon>
                <IFolder />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={selectedProjectText} />
          </ListItemButton>
          {isProjectSelected && sidebarData.menu_items.map(item => {
            const icon = getSidenavIcon(item.name);
            return (
              <ListItemButton
                onClick={() => logger.info(`Side menu item "${item.name}" clicked`)}
                data-active={isActive(item.path)}
                key={item.path}
                component={Link}
                to={item.path}
              >
                {icon != null ? (
                  <Tooltip title={item.label}>
                    <ListItemIcon data-testid={`${item.label}-nav-icon`}>
                      {icon}
                    </ListItemIcon>
                  </Tooltip>
                ) : null}
                <ListItemText data-testid={`${item.label}-nav-text`} primary={item.label}/>
              </ListItemButton>
            );
          })}
        </List>
        <List>
          <ListItemButton
            onClick={() => logger.info(`Side menu item "Support Portal" clicked`)}
            key="Support Portal"
            component="a"
            href="https://damstra.zendesk.com/hc/en-us/categories/4406152250649-Digital-Forms"
            target="_blank"
          >
            <Tooltip title={t('Support Portal')}>
              <ListItemIcon>
                <IQuestion />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={t('Support Portal')}/>
          </ListItemButton>
          <ListItemButton
            key="Help"
            component="a"
            onClick={() => {
              logger.info(`Side menu item "Help" clicked`);
              window.location.href =
                'mailto:formssupport@damstratechnology.com?subject=Forms Support Query';
            }}
            target="_blank"
          >
            <Tooltip title={t('Help')}>
              <ListItemIcon>
                <IChat />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={t('Help')}/>
          </ListItemButton>
          <ListItemButton
            key="Sign Out"
            onClick={handleSignoutClick}
          >
            <Tooltip title={t('Sign Out')}>
              <ListItemIcon>
                <ILogout />
              </ListItemIcon>
            </Tooltip>
            <ListItemText primary={t('Sign Out')}/>
          </ListItemButton>
          {open && (
            <ListItemText
              secondary={sidebarData.version}
              secondaryTypographyProps={{ variant: 'caption' }}
              sx={{ margin: '0px 16px', whiteSpace: 'nowrap' }}
            />
          )}
        </List>
        <Divider />
        <IconButton
          data-testid="sidemenu-toggle"
          sx={{
            margin: '8px',
            marginLeft: 'auto',
          }}
          onClick={() => {
            logger.info('Side menu toggle clicked');
            if (projectDrawerOpen) {
              setProjectDrawerOpen(false);
              setTimeout(() => setOpen(false), 150);
              return;
            }
            handleToggleCollapse();
          }}
          >
          {open ? <IActionCollapse2 /> : <IActionExpand2 />}
        </IconButton>
      </Drawer>
      <Drawer
        open={projectDrawerOpen}
        variant="permanent"
        PaperProps={{
          sx: {
            width: projectDrawerOpen ? 256 : 0,
            transition: theme => theme.transitions.create('width'),
            overflowX: 'hidden',
            overflowY: 'auto',
            position: 'fixed',
            left: 257,
            top: 64,
            height: `calc(100% - 64px)`,
            borderRightWidth: projectDrawerOpen ? 1 : 0,
          },
        }}
      >
        <ProjectMenu />
      </Drawer>
      <React.Fragment>
        <Box sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          ...theme.mixins.toolbar,
        })} />
        {props.children}
      </React.Fragment>
    </React.Fragment>
  );
};

export default Navigation;